import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Upload,
  Alert,
  Image,
  Popconfirm,
  Divider,
  Space,
  Row,
  Switch,
} from "antd";

import { countryCodeOptions } from "@shared/lib/constants";
import {
  GenderEnum,
  ICreateInfluencerRequest,
  IInfluencer,
  INFLUENCER_ACCOUNT_STATUS,
  InfluencerContact,
  InfluencerType,
} from "@shared/lib/types/influencer";
import styles from "../styles.module.scss";

import { CircularProgress } from "@shared/ui";
import { useGetAgencies } from "@entities/agency";
import { ACCOUNT_STATUS } from "@shared/lib/types";
import { AgencyUser, Contact } from "@shared/lib/types/agency";
import { PlusOutlined } from "@ant-design/icons";
import { ToastService } from "@shared/lib/services";

const { Option } = Select;
interface Props {
  onSubmit: (values: AgencyUser) => void;
  isSubmitting: boolean;
  initialValues?: AgencyUser;
  initialRoles: string[];
}

export const ContactForm = ({
  onSubmit,
  isSubmitting,
  initialValues,
  initialRoles,
}: Props) => {
  const [form] = Form.useForm();
  const [accountStatus, setAccountStatus] = useState<ACCOUNT_STATUS>(
    initialValues?.accountStatus || ACCOUNT_STATUS.ACTIVE
  );
  const [newRole, setNewRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState(initialRoles);

  useEffect(() => {
    setAvailableRoles(initialRoles);
  }, [initialRoles]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const addNewRoles = (roleName: string) => {
    if (!roleName.trim() || availableRoles.includes(roleName)) {
      return;
    }

    const updatedRoles = [...availableRoles, roleName];
    setAvailableRoles(updatedRoles);
    setNewRole("");
    form.setFieldsValue({
      role: roleName,
    });
    setDropdownOpen(false);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...initialValues,
        countryCode: initialValues?.countryCode || "+91",
      }}
      onFinish={(values) => {
        onSubmit({
          ...values,
          phoneNumber: values.phoneNumber
            ? values.phoneNumber.replace(/\s/g, "")
            : undefined,
          emailId: values.emailId
            ? values.emailId.replace(/\s/g, "")
            : undefined,
          ...(initialValues?.accountStatus ? { accountStatus } : {}),
        });
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div className={styles.form}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Name  is required" }]}
        >
          <Input placeholder=" Name" />
        </Form.Item>

        <Form.Item
          name="emailId"
          label="Email"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value) {
                  return Promise.reject(new Error("Email is required "));
                }
                if (value && !/^\S+@\S+\.\S+$/.test(value)) {
                  return Promise.reject(new Error("Invalid email format"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder="Email" />
        </Form.Item>
        <Form.Item label="Phone Number">
          <Input.Group compact>
            <Form.Item
              name="countryCode"
              noStyle
              rules={[{ required: false, message: "Country code is required" }]}
            >
              <Select style={{ width: "30%" }}>
                {countryCodeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="phoneNumber"
              noStyle
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject(
                        new Error("Phone number is required ")
                      );
                    }
                    if (value && !/^\d+$/.test(value)) {
                      return Promise.reject(
                        new Error("Phone number must be numeric")
                      );
                    }
                    return Promise.resolve();
                  },
                }),
                {
                  pattern: /^\d+$/,
                  message: "Phone number must be numeric",
                },
              ]}
            >
              <Input style={{ width: "70%" }} placeholder="Phone Number" />
            </Form.Item>
          </Input.Group>
        </Form.Item>
        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: "Role is required" }]}
        >
          <Select
            open={dropdownOpen}
            onDropdownVisibleChange={setDropdownOpen}
            showSearch
            placeholder="Select or create a role"
            optionFilterProp="children"
            allowClear
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space
                  style={{
                    padding: "0 8px 4px",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Input
                    placeholder="Enter new role"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newRole) {
                        e.stopPropagation();

                        e.preventDefault();
                        addNewRoles(newRole);
                      }
                    }}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    disabled={!newRole}
                    onClick={() => addNewRoles(newRole)}
                  >
                    Add Role
                  </Button>
                </Space>
              </>
            )}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {availableRoles.map((role) => (
              <Option key={role} value={role}>
                {role}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {initialValues?.accountStatus && (
          <Form.Item label="Disable">
            <Row justify="space-between">
              <div className={styles.diasbledText}>
                Disable this agency contact.
              </div>
              <Switch
                onChange={(checked) => {
                  setAccountStatus(
                    checked ? ACCOUNT_STATUS.INACTIVE : ACCOUNT_STATUS.ACTIVE
                  );
                }}
                checked={accountStatus === ACCOUNT_STATUS.INACTIVE}
              />
            </Row>
          </Form.Item>
        )}
      </div>
      <div className={styles.footer}>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? "Saving" : "Save"}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default ContactForm;
